import { useEffect, useRef, useState } from "react";
import mermaid from "mermaid";

mermaid.initialize({
  startOnLoad: false,
  theme: "base",
  themeVariables: {
    primaryColor: "#e8f0fe",
    primaryBorderColor: "#1a73e8",
    primaryTextColor: "#202124",
    lineColor: "#5f6368",
    secondaryColor: "#e6f4ea",
    tertiaryColor: "#fef7e0",
    fontFamily: "Inter, sans-serif",
    fontSize: "13px",
  },
  flowchart: { curve: "basis", padding: 12 },
  // "strict" (not "loose"): the chart is model-generated from meeting transcripts
  // (untrusted-derived), and the rendered SVG is injected via innerHTML below.
  // Strict mode disables click/href directives + HTML labels and sanitizes the
  // SVG with Mermaid's bundled DOMPurify, so innerHTML of the output is safe.
  securityLevel: "strict",
  // Don't inject mermaid's built-in "Syntax error in text / mermaid version …"
  // diagram into the DOM on a parse failure — we handle failure ourselves and
  // render nothing instead.
  suppressErrorRendering: true,
});

let counter = 0;

export function sanitizeMermaid(raw: string): string {
  let chart = raw.trim();
  // Strip markdown code fences
  chart = chart.replace(/^```(?:mermaid)?\s*\n?/i, "").replace(/\n?```\s*$/, "");
  // Fix escaped newlines from JSON
  chart = chart.replace(/\\n/g, "\n");
  // Remove empty lines
  chart = chart
    .split("\n")
    .filter((l) => l.trim().length > 0)
    .join("\n");

  // Fix common LLM mermaid mistakes:
  // 1. Unquoted labels with special chars — wrap in quotes
  chart = chart.replace(/\[([^\]"]*[()&<>{}#][^\]"]*)\]/g, (_m, label: string) => {
    return `["${label.replace(/"/g, "'")}"]`;
  });
  // 2. Remove HTML-like tags that break parsing
  chart = chart.replace(/<br\s*\/?>/gi, " ");
  // 3. Fix double-quoted strings inside node labels that use double quotes
  chart = chart.replace(/\["([^"]*)"([^"]*)"([^"]*)"\]/g, "[\"$1'$2'$3\"]");
  // 4. Semicolons at end of lines break some diagram types
  chart = chart.replace(/;\s*$/gm, "");
  // 5. Fix "end" as a node name (reserved keyword)
  chart = chart.replace(/\b(end)\b(?=\s*[\[({])/gi, "End_");

  return chart;
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;
const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

/**
 * Renders a Mermaid chart. When `zoomable` is set, wraps it in a scrollable
 * pan container with a −/reset/+ zoom toolbar (used on the summary + live diagram
 * views where charts can get dense). Default keeps the plain inline behavior.
 */
export function MermaidDiagram({
  chart,
  zoomable = false,
  onError,
  onRendered,
}: {
  chart: string;
  zoomable?: boolean;
  /** Fired once when the chart fails to parse/render (renders nothing). Lets the
   * caller auto-regenerate instead of silently showing a blank diagram. */
  onError?: () => void;
  /** Fired once when the chart renders successfully. */
  onRendered?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);

  // Keep the latest callbacks without re-running the render effect when they change.
  const onErrorRef = useRef(onError);
  const onRenderedRef = useRef(onRendered);
  onErrorRef.current = onError;
  onRenderedRef.current = onRendered;

  useEffect(() => {
    if (!ref.current || !chart) return;
    setFailed(false);
    const id = `mermaid-${++counter}`;
    ref.current.innerHTML = "";
    const cleaned = sanitizeMermaid(chart);
    mermaid
      .render(id, cleaned)
      .then(({ svg }) => {
        if (ref.current) ref.current.innerHTML = svg;
        onRenderedRef.current?.();
      })
      .catch(() => {
        setFailed(true);
        if (ref.current) ref.current.innerHTML = "";
        onErrorRef.current?.();
      });
  }, [chart]);

  if (failed) return null;

  const inner = (
    <div
      ref={ref}
      className="mermaid-container"
      style={zoomable ? { transform: `scale(${zoom})`, transformOrigin: "top left" } : undefined}
    />
  );

  if (!zoomable) return inner;

  return (
    <div className="mermaid-zoom-wrap">
      <div className="mermaid-zoom-toolbar">
        <button
          type="button"
          onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))}
          disabled={zoom <= ZOOM_MIN}
          aria-label="Zoom out"
        >
          −
        </button>
        <button type="button" onClick={() => setZoom(1)} title="Reset zoom">
          {Math.round(zoom * 100)}%
        </button>
        <button
          type="button"
          onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))}
          disabled={zoom >= ZOOM_MAX}
          aria-label="Zoom in"
        >
          +
        </button>
      </div>
      <div className="mermaid-zoom-scroll">{inner}</div>
    </div>
  );
}
